/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */


import * as $ from "jquery";
import {UiCheckBoxConfig, UiCheckBoxSubCommandHandler, UiCheckBoxSubEventSource} from "../../generated/UiCheckBoxConfig";
import {UiFieldEditingMode} from "../../generated/UiFieldEditingMode";
import {UiField} from "./UiField";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {generateUUID} from "../Common";
import {TeamAppsUiComponentRegistry} from "../TeamAppsUiComponentRegistry";
import {UiColorConfig} from "../../generated/UiColorConfig";
import {createUiColorCssString} from "../util/CssFormatUtil";
import {keyCodes} from "trivial-components";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

export class UiCheckBox extends UiField<UiCheckBoxConfig, boolean> implements UiCheckBoxSubEventSource, UiCheckBoxSubCommandHandler {

	private $main: JQuery;
	private $check: JQuery;
	private $label: JQuery;

	protected initialize(config: UiCheckBoxConfig, targetElement: Element, context: TeamAppsUiContext) {
		const uuid = "cb-" + generateUUID();
		this.$main = $(`<div class="UiCheckBox">
                <div class="checkbox-check" tabindex="0"></div>
                <div class="checkbox-label"></div>
            </div>`);
		this.$check = this.$main.find(".checkbox-check");
		this.$label = this.$main.find(".checkbox-label");

		this.setCaption(config.caption);
		this.setBackgroundColor(config.backgroundColor);
		this.setCheckColor(config.checkColor);
		this.setBorderColor(config.borderColor);

		this.$main.mousedown(() => {
			setTimeout(() => this.focus());
		});
		this.$main.click(() => {
			if (this.getEditingMode() === UiFieldEditingMode.DISABLED || this.getEditingMode() === UiFieldEditingMode.READONLY) {
				return;
			}
			this.toggleCommittedValue();
		});
		this.$check.keydown((e) => {
			if (e.keyCode === keyCodes.space) {
				this.toggleCommittedValue();
				e.preventDefault(); // no scroll-down!
			}
		});
		this.$main.appendTo(targetElement);
	}

	isValidData(v: boolean): boolean {
		return v == null || typeof v == "boolean";
	}

	private toggleCommittedValue() {
		this.setCommittedValue(!this.getCommittedValue());
		this.commit(true);
	}

	public getMainDomElement(): JQuery {
		return this.$main;
	}

	setCaption(caption: string): void {
		this.$label.text(caption);
	}

	setBackgroundColor(backgroundColor: UiColorConfig): void {
		this.$check.css("background-color", createUiColorCssString(backgroundColor));
	}

	setCheckColor(checkColor: UiColorConfig): void {
		this.$check.css({
			"color": createUiColorCssString(checkColor)
		});
	}

	setBorderColor(borderColor: UiColorConfig): void {
		this.$check.css({
			"border": `1px solid ${createUiColorCssString(borderColor)}`
		});
	}

	public getFocusableElement(): JQuery {
		return this.$main;
	}

	protected displayCommittedValue(): void {
		let v = this.getCommittedValue();
		this.$check.text(v ? "\ue013" : "");
	}

	focus(): void {
		this.$check.focus();
	}

	destroy(): void {
		this.$main.remove();
	}

	getTransientValue(): boolean {
		return this.getCommittedValue();
	}

	protected onEditingModeChanged(editingMode: UiFieldEditingMode): void {
		if (editingMode === UiFieldEditingMode.DISABLED || editingMode === UiFieldEditingMode.READONLY) {
			this.$main.addClass("disabled");
			this.$check.attr("tabindex", "");
		} else {
			this.$main.removeClass("disabled");
			this.$check.attr("tabindex", "0");
		}
	}

	public static getReadOnlyHtml(config: UiCheckBoxConfig, value: boolean, context: TeamAppsUiContext): string {
		return `<div class="UiCheckBox">
                    <div class="checkbox-check">${value ? '\ue013' : '\u200b'}</div>
                    <div class="checkbox-label">${config.caption || ""}</div>
                </div>`;
	}

	getDefaultValue() {
		return false;
	}

	public valuesChanged(v1: boolean, v2: boolean): boolean {
		return v1 !== v2;
	}

}

TeamAppsUiComponentRegistry.registerFieldClass("UiCheckBox", UiCheckBox);